import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Loader2, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", brand: "Milwaukee", model: "", fault: "", preferred_contact: "", consent: false, website: "" };
const CONTACTS = [{ v: "email", l: "Email" }, { v: "phone", l: "Phone call" }, { v: "whatsapp", l: "WhatsApp" }];

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Please enter your name.";
  if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = "Please enter a valid email address.";
  if (!/^[0-9+()\s-]{7,20}$/.test(f.phone.trim())) e.phone = "Please enter a valid phone number.";
  if (!f.brand.trim()) e.brand = "Please enter the tool brand.";
  if (!f.model.trim()) e.model = "Please enter the model, e.g. M18 FHIWF34.";
  if (f.fault.trim().length < 10) e.fault = "Please describe the fault in a little more detail.";
  if (!f.preferred_contact) e.preferred_contact = "Please choose how we should contact you.";
  if (!f.consent) e.consent = "Please agree so we can use your details to reply.";
  return e;
};

const inputCls = "w-full h-14 bg-[var(--ink)] border border-[var(--line)] px-4 text-base text-white placeholder:text-[#8a919c] transition-[border-color,box-shadow] duration-200 focus:outline-none focus:border-[var(--red)] focus:shadow-[0_0_0_3px_rgba(227,0,27,0.2)]";

const Field = ({ id, label, error, hint, children }) => (
  <div>
    <label htmlFor={id} className="block font-mono text-xs uppercase tracking-[0.18em] text-[var(--grey)] mb-2">{label}</label>
    {children}
    {hint && !error && <p className="mt-2 text-xs text-[var(--mute)]">{hint}</p>}
    {error && <p id={`${id}-error`} role="alert" className="mt-2 text-sm text-[#ff6b7a]" data-testid={`error-${id}`}>{error}</p>}
  </div>
);

export const EnquiryForm = () => {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const startedAt = useRef(Date.now());
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const aria = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${k}-error` : undefined });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) {
      document.getElementById(Object.keys(errs)[0])?.focus();
      return;
    }
    setStatus("sending");
    try {
      const res = await axios.post(`${API}/enquiries`, { ...f, started_at: startedAt.current });
      setMessage(res.data?.reference ?? "");
      setStatus("success");
      setF(EMPTY);
    } catch (err) {
      const d = err.response?.data?.detail;
      setMessage(typeof d === "string" ? d : "Sorry, something went wrong sending your enquiry. Please try again or message us on WhatsApp.");
      setStatus("error");
    }
  };

  if (status === "success") return (
    <div className="metal metal-edge chamfer border border-[var(--line)] p-8 md:p-12" role="status" data-testid="enquiry-success">
      <CheckCircle2 className="h-10 w-10 text-[var(--wa)]" aria-hidden />
      <h2 className="mt-6 font-display font-black uppercase text-4xl md:text-5xl leading-[0.95]">Enquiry sent</h2>
      <p className="mt-4 text-base text-[var(--grey)] leading-relaxed max-w-md">Thanks, we've received your repair enquiry and will get back to you using your preferred contact method. A copy of your enquiry has been emailed to you (check your spam folder if it doesn't arrive).</p>
      {message && <p className="mt-4 font-mono text-xs uppercase tracking-[0.18em] text-[var(--mute)]">Reference: <span className="text-white" data-testid="enquiry-reference">{message}</span></p>}
      <p className="mt-4 text-sm text-[var(--grey)]">Got photos of the fault? Send them on WhatsApp and mention your reference.</p>
      <button type="button" onClick={() => { setStatus("idle"); startedAt.current = Date.now(); }} className="mt-8 h-12 px-6 border border-[var(--line)] font-display font-extrabold uppercase tracking-[0.14em] text-sm hover:border-white transition-colors" data-testid="enquiry-new-btn">Send another enquiry</button>
    </div>
  );

  return (
    <form onSubmit={submit} noValidate className="metal metal-edge chamfer border border-[var(--line)] p-6 md:p-10 grid gap-6" data-testid="enquiry-form" aria-label="Repair enquiry form">
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website">Leave this empty</label>
        <input id="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <Field id="name" label="Your name" error={errors.name}>
          <input id="name" autoComplete="name" className={inputCls} value={f.name} onChange={set("name")} {...aria("name")} data-testid="input-name" />
        </Field>
        <Field id="phone" label="Phone number" error={errors.phone}>
          <input id="phone" type="tel" autoComplete="tel" className={inputCls} value={f.phone} onChange={set("phone")} {...aria("phone")} data-testid="input-phone" />
        </Field>
      </div>
      <Field id="email" label="Email address" error={errors.email}>
        <input id="email" type="email" autoComplete="email" className={inputCls} value={f.email} onChange={set("email")} {...aria("email")} data-testid="input-email" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-6">
        <Field id="brand" label="Tool brand" error={errors.brand}>
          <input id="brand" className={inputCls} value={f.brand} onChange={set("brand")} {...aria("brand")} data-testid="input-brand" />
        </Field>
        <Field id="model" label="Tool model" error={errors.model} hint="Usually on the label under the battery.">
          <input id="model" className={inputCls} placeholder="e.g. M18 FHIWF34" value={f.model} onChange={set("model")} {...aria("model")} data-testid="input-model" />
        </Field>
      </div>
      <Field id="fault" label="What's wrong with it?" error={errors.fault}>
        <textarea id="fault" rows={5} maxLength={2000} className={`${inputCls} h-auto py-3 resize-y`} placeholder="What is the tool doing (or not doing)? When did it start?" value={f.fault} onChange={set("fault")} {...aria("fault")} data-testid="input-fault" />
      </Field>
      <fieldset>
        <legend className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--grey)] mb-3">Preferred contact method</legend>
        <div className="grid grid-cols-3 gap-2" role="radiogroup">
          {CONTACTS.map((c) => (
            <label key={c.v} className={`h-12 flex items-center justify-center text-sm font-semibold border cursor-pointer transition-colors ${f.preferred_contact === c.v ? "border-[var(--red)] bg-[var(--red)] text-white" : "border-[var(--line)] text-[var(--grey)] hover:border-white"}`} data-testid={`contact-${c.v}`}>
              <input type="radio" name="preferred_contact" value={c.v} checked={f.preferred_contact === c.v} onChange={set("preferred_contact")} className="sr-only" />
              {c.l}
            </label>
          ))}
        </div>
        {errors.preferred_contact && <p role="alert" className="mt-2 text-sm text-[#ff6b7a]" data-testid="error-preferred_contact">{errors.preferred_contact}</p>}
      </fieldset>
      <div className="border-t border-[var(--line)] pt-6">
        <p className="text-sm text-[var(--grey)] leading-relaxed" data-testid="privacy-notice">
          <strong className="text-white">Privacy notice:</strong> NE Tool Repair will use the details you give here only to reply to your enquiry and arrange any repair you agree to. We don't share them for marketing. See our <Link to="/privacy" className="underline text-white" data-testid="form-privacy-link">privacy &amp; cookies page</Link> for how long we keep them and your rights.
        </p>
        <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="consent-label">
          <input id="consent" type="checkbox" checked={f.consent} onChange={set("consent")} {...aria("consent")} className="mt-1 h-5 w-5 accent-[var(--red)] shrink-0" data-testid="input-consent" />
          <span className="text-sm text-white">I agree to NE Tool Repair using these details to respond to my enquiry.</span>
        </label>
        {errors.consent && <p id="consent-error" role="alert" className="mt-2 text-sm text-[#ff6b7a]" data-testid="error-consent">{errors.consent}</p>}
      </div>
      {status === "error" && (
        <div role="alert" className="flex gap-3 border border-[#ff6b7a]/60 bg-[#ff6b7a]/10 p-4 text-sm text-white" data-testid="enquiry-error">
          <AlertTriangle className="h-5 w-5 text-[#ff6b7a] shrink-0" aria-hidden /> {message}
        </div>
      )}
      <button type="submit" disabled={status === "sending"} className="group h-14 w-full sm:w-auto sm:justify-self-start px-8 chamfer-sm bg-[var(--red)] hover:bg-[var(--red-hi)] disabled:opacity-70 transition-colors inline-flex items-center justify-center gap-3 font-display font-extrabold uppercase tracking-[0.14em]" data-testid="enquiry-submit-btn">
        {status === "sending" ? <><Loader2 className="h-5 w-5 animate-spin" aria-hidden /> Sending…</> : <>Send enquiry <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden /></>}
      </button>
    </form>
  );
};
